import { useState } from 'react';
import { SearchField } from './Field';
import { IconCheck, IconGrid, IconList } from './icons';
import { MenuAnchor, MenuSurface, useDismiss } from './Menu';
import { VisIcon, type Visibility } from './Visibility';

/**
 * Browse Bar.dc.html: All/Saved tabs with counts, text filter (260px), the
 * Visibility select (All / Private / Shared / Public), grid/list toggle.
 * Every browsable page uses it (PRODUCT_RULES); Home is exempt.
 */
export type BrowseTab = 'all' | 'saved';
export type BrowseView = 'grid' | 'list';
export type BrowseShare = 'all' | Visibility;

export interface BrowseState {
  tab: BrowseTab;
  view: BrowseView;
  share: BrowseShare;
  query: string;
}

const SHARE_LABEL: Record<BrowseShare, string> = { all: 'Visibility', private: 'Private', link: 'Shared', public: 'Public' };

export function BrowseBar({ allCount, savedCount, state, placeholder = 'Filter', onChange }: { allCount: number; savedCount: number; state: BrowseState; placeholder?: string; onChange: (s: BrowseState) => void }) {
  const [menu, setMenu] = useState(false);
  useDismiss(menu, () => setMenu(false));
  const set = (patch: Partial<BrowseState>) => onChange({ ...state, ...patch });
  const isGrid = state.view === 'grid';
  const tab = (id: BrowseTab, label: string, count: number) => {
    const on = state.tab === id;
    return (
      <div
        onClick={() => set({ tab: id })}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          whiteSpace: 'nowrap',
          fontSize: 14,
          fontWeight: 500,
          color: on ? '#0f0f10' : '#6b6b70',
          borderBottom: `2px solid ${on ? '#0f0f10' : 'transparent'}`,
          marginBottom: -13,
          paddingBottom: 13,
          cursor: 'pointer',
        }}
      >
        {label} <span style={{ color: '#8a8a8f', fontWeight: 400 }}>{count}</span>
      </div>
    );
  };
  return (
    <div className="tr-wrap-sm" style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 16, borderBottom: '1px solid #ebebea', paddingBottom: 12 }}>
      <div style={{ display: 'flex', gap: 22, alignItems: 'stretch' }}>
        {tab('all', 'All', allCount)}
        {tab('saved', 'Saved', savedCount)}
      </div>
      <div className="tr-basis-full-sm" style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'center' }}>
        <div className="tr-grow-sm" style={{ width: 260 }}>
          <SearchField value={state.query} onChange={(query) => set({ query })} placeholder={placeholder} width="100%" h={30} />
        </div>
        <MenuAnchor>
          <div
            onClick={(e) => {
              e.stopPropagation();
              setMenu(!menu);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 30,
              boxSizing: 'border-box',
              padding: '0 10px',
              border: `1px solid ${state.share === 'all' ? '#e3e3e1' : '#0f0f10'}`,
              borderRadius: 6,
              fontSize: 12.5,
              background: state.share === 'all' ? '#fff' : '#f2f2f0',
              color: '#0f0f10',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {state.share !== 'all' ? <VisIcon vis={state.share} /> : null}
            {SHARE_LABEL[state.share]}{' '}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
          {menu ? (
            <MenuSurface top={34} right={0} width={150}>
              {(['all', 'private', 'link', 'public'] as BrowseShare[]).map((v) => {
                const on = state.share === v;
                return (
                  <div
                    key={v}
                    className="tr-hover-track"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenu(false);
                      set({ share: v });
                    }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: 5, fontSize: 13, cursor: 'pointer', background: on ? '#f6f6f4' : 'transparent' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3f3f44' }}>
                      {v !== 'all' ? <VisIcon vis={v} /> : null}
                      <span style={{ color: '#0f0f10' }}>{v === 'all' ? 'All' : SHARE_LABEL[v]}</span>
                    </span>
                    {on ? <IconCheck size={13} sw={2.5} color="#0f0f10" /> : null}
                  </div>
                );
              })}
            </MenuSurface>
          ) : null}
        </MenuAnchor>
        <div style={{ display: 'flex', height: 30, boxSizing: 'border-box', border: '1px solid #e3e3e1', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
          <div title="Grid" onClick={() => set({ view: 'grid' })} style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', cursor: 'pointer', background: isGrid ? '#f2f2f0' : '#fff', color: isGrid ? '#0f0f10' : '#8a8a8f' }}>
            <IconGrid size={14} />
          </div>
          <div title="List" onClick={() => set({ view: 'list' })} style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', cursor: 'pointer', borderLeft: '1px solid #e3e3e1', background: isGrid ? '#fff' : '#f2f2f0', color: isGrid ? '#8a8a8f' : '#0f0f10' }}>
            <IconList size={14} />
          </div>
        </div>
      </div>
    </div>
  );
}
